import { Briefcase, ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import type { FolderTab } from '../../../../components/FolderTabs';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { PostingStatusText } from '../components/PostingStatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { applicantsApi } from '../applicantsApi';
import type { JobPostingListItem } from '../applicantTypes';
import { ApplicantsPanel } from '../components/ApplicantsPanel';

type TabKey = 'info' | 'applicants';

/**
 * One job posting, on the **header** layout — the post it advertises, and a folder tab of the
 * people who applied.
 *
 * **The posting itself is Vacancies' record**: it is made by tagging an item there and ended by
 * removing the tag or filling the item. So the Info card carries no Edit and links out, and the
 * banner states the posting's state with no action of its own. What this module writes is the
 * Applicants tab.
 */
export function JobPostingProfilePage() {
  const { postingId = '' } = useParams<{ postingId: string }>();
  const navigate = useNavigate();

  const [posting, setPosting] = useState<JobPostingListItem | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<TabKey>('info');
  const [historyOpen, setHistoryOpen] = useState(false);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    applicantsApi
      .getPosting(postingId, controller.signal)
      .then((loaded) => {
        setPosting(loaded);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoadError(caught instanceof ApiError ? caught : new ApiError('The job posting could not be loaded.', 0));
      });

    return () => controller.abort();
  }, [postingId, reloadKey]);

  const tabs: FolderTab<TabKey>[] = [
    { key: 'info', label: 'Info' },
    {
      key: 'applicants',
      label: (
        <>
          Applicants
          <CountBadge count={posting?.applicantCount ?? 0} tone="slate" />
        </>
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      <Breadcrumb
        segments={[
          'Human Resources',
          'Recruitment',
          { label: 'Applicants', to: '/applicants' },
          posting ? posting.itemNumber : '',
        ]}
      />

      {loadError && !posting ? (
        <ErrorState
          message={loadError.message}
          correlationId={loadError.correlationId}
          onRetry={loadError.isNotFound ? undefined : reload}
        />
      ) : !posting ? (
        <ProfileHeaderSkeleton tabs={tabs.length} />
      ) : (
        <ProfileHeaderShell<TabKey>
          id="job-posting-profile"
          tabs={tabs}
          activeTab={tab}
          onTabChange={setTab}
          panel={tab === 'applicants' ? 'bleed' : 'cards'}
          header={
            <ProfileHeader
              id="job-posting-profile-header"
              icon={<Briefcase className="h-5 w-5" />}
              title={posting.positionTitle}
              meta={[
                posting.itemNumber,
                <span key="unit" className="font-sans">
                  {posting.orgUnitName}
                </span>,
              ]}
              status={
                <PostingStatusText posting={posting} />
              }
              createdBy={posting.createdByName}
              createdAt={posting.createdAt}
              settings={{ onHistory: () => setHistoryOpen(true) }}
            />
          }
          banner={
            <ProfileBanner
              id="job-posting-profile-banner"
              tone={posting.isActive && posting.isPublished && !posting.isCompleted ? 'emerald' : posting.isActive && !posting.isPublished ? 'blue' : 'slate'}
              message={
                posting.isActive && posting.isCompleted
                  ? "This posting is completed — an endorsed applicant's appointment is recorded under Deliberate. It takes no new applicants; withdrawing the endorsement reopens it."
                  : posting.isActive && !posting.isPublished
                  ? 'This posting is not published yet. It takes no applicants until it is published under Vacancies.'
                  : posting.isActive
                  ? posting.applicantCount === 0
                    ? 'Open for applications. Nobody has applied yet.'
                    : `Open for applications. ${posting.applicantCount} applicant${posting.applicantCount === 1 ? '' : 's'} so far.`
                  : posting.isFilled
                    ? 'This posting has ended — its item is filled. It takes no new applicants; those recorded are kept.'
                    : 'This posting has ended — its job posting tag was removed or the item was filled. It takes no new applicants; those recorded are kept.'
              }
            />
          }
        >
          {tab === 'info' ? (
            <>
              <ProfileCard
                id="job-posting-profile-post"
                title="Post"
                // The plantilla's and Vacancies' record: no Edit here, links out to where it lives.
                menuItems={[
                  {
                    key: 'open-vacancy',
                    label: 'Open in Vacancies',
                    icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
                    permission: 'vacancies.read',
                    onClick: () => navigate(`/vacancies/${posting.plantillaItemId}`),
                  },
                ]}
              >
                <PreviewTable variant="table-display" rows={postRows(posting)} />
              </ProfileCard>

              {/* Never editable: a record of what happened. */}
              <ProfileCard id="job-posting-profile-audit" title="Audit">
                <PreviewTable variant="table-display" rows={auditRows(posting)} />
              </ProfileCard>
            </>
          ) : (
            <ApplicantsPanel posting={posting} onChanged={reload} />
          )}
        </ProfileHeaderShell>
      )}

      {posting ? (
        <HistoryLogsModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          module="applicants"
          // A RECORD's history is the id alone — every add names the posting in its route.
          entityId={posting.id}
        />
      ) : null}
    </div>
  );
}

/** What the header does not already say — title, item number and office are the header's. */
function postRows(posting: JobPostingListItem): PreviewRow[] {
  return [
    { label: 'Salary grade', value: posting.salaryGradeLabel },
    {
      label: 'Monthly salary',
      value: posting.monthlyAmount != null ? formatMoney(posting.monthlyAmount) : null,
      mono: true,
    },
    { label: 'Education', value: posting.education },
    { label: 'Training', value: posting.training },
    { label: 'Experience', value: posting.experience },
    { label: 'Eligibility', value: posting.eligibilityName },
    { label: 'Other expectation', value: posting.otherExpectations },
  ];
}

/** The update half — when the posting ended, and by whom. Created is the header's. */
function auditRows(posting: JobPostingListItem): PreviewRow[] {
  return [
    { label: 'Updated by', value: posting.updatedByName },
    { label: 'Updated', value: posting.updatedAt != null ? formatDateTime(posting.updatedAt) : null, mono: true },
  ];
}
